import { Activity, Bot, Maximize2, MessagesSquare, Minimize2, Users, X } from "lucide-react";
import type { ReactNode } from "react";
import { Tip } from "@/components/ui/overlay";
import { desktop } from "@/desktop/bridge";
import { useAiProviders } from "@/desktop/chat";
import { useDesktopUi, type RightTab } from "@/desktop/ui";
import { cn } from "@/lib/utils";
import { ProviderMark } from "./chat-header";

interface TabInfo {
  id: RightTab;
  /** Nome da aba: aparece só na aba aberta (nas outras, na dica). */
  label: string;
  /** Equipe (pessoas, chat, atividade) ou IA (agente, Claude, Copilot): grupos separados. */
  group: "team" | "ai";
  title?: string;
  icon: ReactNode;
  badge?: number;
  /** Ponto pulsando (ex.: alguém pediu ajuda). */
  alert?: boolean;
}

const AI_LABEL = { claude: "Claude Code", copilot: "Copilot" } as const;

/**
 * Abas da lateral direita, enxutas: só ícones, agrupados (Equipe | IA), e o nome apenas na aba
 * aberta — o espaço fica para o conteúdo. O fechar fica à direita.
 */
export function RightPanelFrame({
  tabs,
  active,
  expandable = false,
  children,
}: {
  tabs: TabInfo[];
  active: RightTab;
  /** Claude Code e Copilot podem abrir em tela cheia (só dentro do projeto). */
  expandable?: boolean;
  children: ReactNode;
}) {
  const set = useDesktopUi((s) => s.set);
  const full = useDesktopUi((s) => s.aiFull);
  const canExpand = expandable && (active === "claude" || active === "copilot");
  const current = tabs.find((t) => t.id === active);
  const tab = (t: TabInfo) => {
    const on = t.id === active;
    return (
      <Tip key={t.id} label={t.title ?? t.label}>
        <button
          role="tab"
          aria-selected={on}
          aria-label={t.label}
          onClick={() => set({ side: t.id })}
          className={cn(
            "relative flex h-7 shrink-0 items-center gap-1.5 rounded-md text-[12.5px] font-medium outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/60",
            on
              ? "bg-bg-emphasis px-2 text-fg"
              : "w-7 justify-center text-fg-subtle hover:bg-bg-muted hover:text-fg",
          )}
        >
          <span className="flex h-4 w-4 items-center justify-center">{t.icon}</span>
          {on ? <span className="max-w-[110px] truncate">{t.label}</span> : null}
          {t.badge ? (
            // Não lidas "em cima" da aba, com um pulo a cada mensagem nova.
            <span
              key={t.badge}
              className="absolute -right-1 -top-1 z-10 inline-flex h-[16px] min-w-[16px] animate-pop-in items-center justify-center rounded-full bg-fg px-1 text-[9.5px] font-bold leading-none text-fg-inverse ring-2 ring-panel"
              aria-label={`${t.badge} não lidas`}
            >
              {t.badge > 99 ? "99+" : t.badge}
            </span>
          ) : null}
          {t.alert ? (
            <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 animate-pulse-dot rounded-full bg-[var(--warning)]" />
          ) : null}
        </button>
      </Tip>
    );
  };
  const team = tabs.filter((t) => t.group === "team");
  const ai = tabs.filter((t) => t.group === "ai");
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        className="flex h-10 shrink-0 items-center gap-1 overflow-x-auto border-b border-panel-border pl-1.5 pr-1 [scrollbar-width:none]"
        role="tablist"
        aria-label={current ? `Lateral direita: ${current.label}` : "Lateral direita"}
      >
        {team.map(tab)}
        {team.length && ai.length ? (
          <span className="mx-0.5 h-4 w-px shrink-0 bg-border" aria-hidden />
        ) : null}
        {ai.map(tab)}
        {canExpand ? (
          <Tip label={full ? "Sair da tela cheia" : "Abrir em tela cheia"}>
            <button
              onClick={() => set({ aiFull: !full })}
              className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-bg-muted hover:text-fg"
              aria-label={full ? "Sair da tela cheia" : "Abrir em tela cheia"}
              aria-pressed={full}
            >
              {full ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </button>
          </Tip>
        ) : null}
        <Tip label="Fechar a lateral (Ctrl+Alt+B)">
          <button
            onClick={() => set({ side: null, aiFull: false })}
            className={cn(
              canExpand ? "" : "ml-auto",
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-bg-muted hover:text-fg",
            )}
            aria-label="Fechar lateral"
          >
            <X className="h-4 w-4" />
          </button>
        </Tip>
      </div>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}

/** Abas disponíveis: Pessoas e Chat do time (no projeto) e os chats de IA dos plugins instalados. */
export function useRightTabs(opts: {
  inProject: boolean;
  online?: number;
  unread?: number;
  help?: boolean;
  /** Visualizadores não usam IA: sem as abas do agente, do Claude e do Copilot. */
  ai?: boolean;
}): TabInfo[] {
  const installed = useAiProviders((s) => s.installed);
  const tabs: TabInfo[] = [];
  if (opts.inProject) {
    tabs.push({
      id: "people",
      group: "team",
      label: opts.online && opts.online > 1 ? `Pessoas · ${opts.online}` : "Pessoas",
      title: opts.online ? `Pessoas no projeto (${opts.online} online)` : "Pessoas no projeto",
      icon: <Users className="h-3.5 w-3.5" />,
      alert: opts.help,
    });
    tabs.push({
      id: "team",
      group: "team",
      label: "Chat",
      title: "Chat do time",
      icon: <MessagesSquare className="h-3.5 w-3.5" />,
      badge: opts.unread,
    });
    tabs.push({
      id: "activity",
      group: "team",
      label: "Atividade",
      title: "Atividade do projeto: quem está aqui, entradas e saídas, alterações recentes",
      icon: <Activity className="h-3.5 w-3.5" />,
    });
    // Agente com a chave de IA da pessoa: a chave fica no cofre do app desktop.
    if (desktop?.assistant && opts.ai !== false) {
      tabs.push({
        id: "agent",
        group: "ai",
        label: "Agente",
        title: "Agente de código com a sua chave de IA (Ctrl+Shift+A)",
        icon: <Bot className="h-3.5 w-3.5" />,
      });
    }
  }
  for (const p of opts.ai === false ? [] : installed) {
    tabs.push({
      id: p,
      group: "ai",
      label: AI_LABEL[p],
      title: p === "claude" ? "Chat com o Claude" : "GitHub Copilot Chat",
      icon: <ProviderMark provider={p} className="h-4 w-4" />,
    });
  }
  return tabs;
}
